import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAdvertiser } from '../contexts/AdvertiserContext';
import { useAuth } from '../contexts/AuthContext';
import { clientsApi } from '../services/api';
import { getBranding } from '../config/branding';

/**
 * "New Campaign" button that gates navigation when the selected client's
 * advertiser isn't ready yet (creation_status != 'complete').
 *
 * Behaviour:
 *  - "All clients" view → navigate immediately (no advertiser context to gate on).
 *  - Selected client + ready → navigate immediately.
 *  - Selected client + not ready → show modal with status + (admin) retry button.
 */
interface Props {
  className?: string;
  label?: string;
}

const NewCampaignButton: React.FC<Props> = ({ className = 'btn btn-primary', label }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { selectedAdvertiserId, isAllClients } = useAdvertiser();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [status, setStatus] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [retrying, setRetrying] = useState(false);

  // Refresh status whenever the selected advertiser changes, so the gate
  // is up-to-date without forcing a full page reload.
  useEffect(() => {
    if (!selectedAdvertiserId || isAllClients) {
      setStatus(null);
      return;
    }
    let cancelled = false;
    clientsApi.getCreationStatus(selectedAdvertiserId).then((r) => {
      if (!cancelled) {
        setStatus(r.creation_status);
      }
    }).catch(() => {
      // Network failure shouldn't block — assume ready and let the
      // create endpoint enforce.
    });
    return () => { cancelled = true; };
  }, [selectedAdvertiserId, isAllClients]);

  // 'synced' (advertiser synced from the platform) and 'complete' (created via UI) are both
  // fully-ready states; null = unknown (network fail) → assume ready and let the create
  // endpoint enforce. Only the genuinely in-flight/failed states gate the button.
  const READY_STATES = ['complete', 'synced'];
  const ready = isAllClients || status === null || READY_STATES.includes(status);

  const onClick = () => {
    if (ready) {
      navigate('/campaigns/new');
    } else {
      setShowModal(true);
    }
  };

  const onRetry = async () => {
    if (!selectedAdvertiserId) return;
    setRetrying(true);
    try {
      const r = await clientsApi.retryCreation(selectedAdvertiserId);
      setStatus(r.creation_status || null);
      if (r.success) {
        setShowModal(false);
        // Force the surrounding page to refresh so the new real advertiser_id
        // propagates everywhere (URLs, list endpoints, etc.).
        window.location.reload();
      }
    } catch {
      // Swallow — don't surface raw API errors to the user.
    } finally {
      setRetrying(false);
    }
  };

  return (
    <>
      <button className={className} onClick={onClick}>
        {getBranding().edition !== 'sense'
          ? <span className="dot"></span>
          : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
            </svg>
          )}
        {label ?? t('campaigns.newCampaign')}
      </button>
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 460 }}>
            <h3 style={{ marginTop: 0 }}>{t('campaigns.clientSetupTitle')}</h3>
            <p style={{ color: '#6c757d', lineHeight: 1.55 }}>
              {t('campaigns.clientSetupBody')}
            </p>
            <p style={{ color: '#6c757d', fontSize: 13 }}>
              {t('campaigns.currentStatusLabel')} <b>{status || t('campaigns.unknownStatus')}</b>
              {status === 'error' && t('campaigns.lastAttemptFailed')}
            </p>
            <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>{t('campaigns.close')}</button>
              {isAdmin && (
                <button className="btn btn-primary" disabled={retrying} onClick={onRetry}>
                  {retrying ? t('campaigns.retrying') : t('campaigns.retryCreation')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default NewCampaignButton;
